import { useId, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { useLocale } from '../../i18n';
import { errorMessage } from '../../i18n/errorMessage';
import { sendMemberInvitation, type MemberInvitationSent } from '../../lib/api/memberInvitation';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «INVITER TIL SPILLER-APPEN» — BY E-MAIL, WITH THE PLAYER'S CONSENT (#1061).
//
// It used to mint a join code and put the player on the club's roster at once,
// whoever they were. #197 Q4 (decided 2026-10-23): a club gets a platform
// player onto its roster only with the player's consent. So this sends an
// invitation to an address, and NOTHING CHANGES until the person accepts from
// that mailbox — which is what the screen says, so nobody believes the player
// is a member the moment they press «Send».
//
// THE ADDRESS: the player's stored one, where the club sees it, or one the
// operator types. Either way it is the club saying «this mailbox is theirs»;
// the person then proves it.
// ═══════════════════════════════════════════════════════════════════

export interface InvitePlayerModalProps {
  /** The player's legacy id — what every row in this view carries. */
  playerId: string;
  playerName: string;
  /** The stored address, where the club sees one (#1060). Pre-fills the field. */
  playerEmail?: string;
  onClose: () => void;
}

export function InvitePlayerModal({
  playerId,
  playerName,
  playerEmail = '',
  onClose,
}: InvitePlayerModalProps) {
  const { t, formatDate } = useLocale();
  const uid = safeId('invite-player', useId());
  const ids = { title: `${uid}-title`, email: `${uid}-email` };
  const [email, setEmail] = useState(playerEmail);
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'working' }
    | { kind: 'done'; invitation: MemberInvitationSent }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  const send = async () => {
    if (!email.trim().includes('@')) {
      setState({ kind: 'failed', message: t('players.invite.emailRequired') });
      return;
    }
    setState({ kind: 'working' });
    const res = await sendMemberInvitation(playerId, email);
    if (res.state === 'ok') setState({ kind: 'done', invitation: res.invitation });
    else {
      // The server's CODE becomes a sentence here (#519); the raw code never
      // reaches the screen.
      setState({
        kind: 'failed',
        message:
          res.state === 'refused' ? errorMessage(t, res.reason) : t('players.invite.unreachable'),
      });
    }
  };

  const done = state.kind === 'done' ? state.invitation : null;

  return (
    <Dialog
      labelledBy={ids.title}
      onClose={onClose}
      showClose={false}
      // A stray click outside must not throw away an address somebody typed.
      closeOnBackdropClick={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      className="card p-5 w-full max-w-md max-h-[92vh] overflow-y-auto"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 id={ids.title} className="font-semibold text-sm flex items-center gap-2">
          <UserPlus size={14} aria-hidden="true" />{' '}
          {t('players.invite.title', { name: playerName })}
        </h2>
        <button onClick={onClose} className="p-1 text-ink-3" aria-label={t('players.invite.close')}>
          <X size={16} />
        </button>
      </div>

      {!done && (
        <form
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <p className="text-[12px] text-ink-3">{t('players.invite.explainConsent')}</p>
          <label htmlFor={ids.email} className="block text-[11px] text-ink-3 mt-3">
            {t('players.invite.emailLabel')}
          </label>
          <input
            id={ids.email}
            type="email"
            className="input mt-1 w-full"
            value={email}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
            data-testid="invite-email"
          />
          {state.kind === 'failed' && (
            <p role="alert" className="text-sm text-rose-700 mt-3" data-testid="invite-failed">
              {state.message}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="btn-outline text-xs">
              {t('players.invite.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary text-xs"
              disabled={state.kind === 'working'}
              data-testid="invite-send"
            >
              {state.kind === 'working' ? t('players.invite.working') : t('players.invite.send')}
            </button>
          </div>
        </form>
      )}

      {done && (
        <div className="mt-3" role="status" data-testid="invite-done">
          {done.sent ? (
            <>
              <p className="text-sm">
                {t('players.invite.sent', {
                  email: email.trim(),
                  date: formatDate(new Date(done.expiresAt), { dateStyle: 'long' }),
                })}
              </p>
              <p className="text-[12px] text-ink-3 mt-2" data-testid="invite-consent-note">
                {done.kind === 'consent'
                  ? t('players.invite.notYetMember', { club: done.club.name ?? '' })
                  : t('players.invite.claimNote')}
              </p>
            </>
          ) : (
            <p className="text-sm text-rose-700" data-testid="invite-not-sent">
              {t('players.invite.notSent')}
            </p>
          )}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline text-xs"
              data-testid="invite-close"
            >
              {t('players.invite.close')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
